import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./EditFormComponent.css";
import FieldRenderer from "./FieldRenderer";
import PasswordModal from "./PasswordModal";
import { getSanitizedPayload } from "./utils";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import TableLoader from "../TableComponent/TableLoader";
import DynamicButton from "../DynamicButton/DynamicButton";

const EditFormComponent = ({
  fields,
  formData,
  onChange,
  modelId,
  Put,
  file,
  model,
  onSuccess,
}) => {
  const isCreate = !modelId;
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [decryptedValue, setDecryptedValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [allIds, setAllIds] = useState([]);
  const [readOnlyLink, setReadOnlyLink] = useState(false);

  // NEW: bootstrapping/loading for dropdown APIs + formData presence
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [dropdownOptions, setDropdownOptions] = useState({});
  const navigate = useNavigate();

  // ---- Submit (memoized for Ctrl+S listener) ----
  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const isCreate = !modelId;
    const url = isCreate ? `${Put}` : `${Put}/${modelId}`;
    const method = isCreate ? "POST" : "PUT";

    try {
      const payload = getSanitizedPayload(formData, fields);
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(
          isCreate ? "✅ Created successfully" : "✅ Data updated successfully",
          { position: "top-right", autoClose: 1500 }
        );

        setTimeout(() => {
          // if (isCreate && data?._id) {
          //   navigate(`/edit/${model}/${data._id}`);
          // } else {
          //   navigate(`/table/${model}`);
          // }
          if (onSuccess) {
            onSuccess(data); // tell parent to refresh table or close modal
          }
        }, 1600);
      } else {
        toast.error(
          data?.message || (isCreate ? "Create failed" : "Update failed"),
          {
            position: "top-right",
          }
        );
      }
    } catch (err) {
      console.error("🔥 Submit error:", err);
      toast.error("Error submitting form", { position: "top-right" });
    } finally {
      setIsSubmitting(false);
    }
  }, [Put, model, modelId, formData, fields, isSubmitting, navigate]);

  // ---- Fetch all IDs once (for next/prev navigation) ----
  useEffect(() => {
    const fetchIds = async () => {
      try {
        const res = await fetch(Put); // assumes GET /api/<model> returns list
        const data = await res.json();
        if (Array.isArray(data)) {
          setAllIds(data.map((d) => d._id));
        }
      } catch (err) {
        console.error("Failed to load record IDs:", err);
      }
    };
    if (!isCreate) fetchIds();
  }, [Put, isCreate]);

  useEffect(() => {
    const currentPath = window.location.pathname;
    // If the path includes "LinkUaUcExecution" (or add more if needed)
    if (currentPath.includes("/edit/LinkUaUcExecution")) {
      setReadOnlyLink(true);
    } else {
      setReadOnlyLink(false);
    }
  }, []);

  // ---- Next/Prev navigation ----
  const goToNext = () => {
    if (!modelId || allIds.length === 0) return;
    const idx = allIds.indexOf(modelId);
    if (idx >= 0 && idx < allIds.length - 1) {
      navigate(`/edit/${model}/${allIds[idx + 1]}`);
    } else {
      toast.info("Already at the last record");
    }
  };

  const goToPrev = () => {
    if (!modelId || allIds.length === 0) return;
    const idx = allIds.indexOf(modelId);
    if (idx > 0) {
      navigate(`/edit/${model}/${allIds[idx - 1]}`);
    } else {
      toast.info("Already at the first record");
    }
  };

  // ---- Ctrl/Cmd + S -> Save ----
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        handleSubmit();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleSubmit]);

  const apiSignature = useMemo(
    () =>
      JSON.stringify(
        (fields || [])
          .filter((f) => !!f.api)
          .map((f) => f.api)
          .sort()
      ),
    [fields]
  );

  // ---- Dropdowns (cache by both field *and* api to support different consumers) ----
  useEffect(() => {
    let cancelled = false;

    const fetchDropdowns = async () => {
      // Gather fields with APIs
      const apiFields = (fields || []).filter((f) => !!f.api);

      // If nothing to fetch and we already have formData, we’re ready
      if (apiFields.length === 0) {
        setIsBootstrapping(false);
        return;
      }

      setIsBootstrapping(true);
      const newOptions = {};
      const tasks = apiFields.map(async (f) => {
        try {
          const res = await fetch(f.api);
          const data = await res.json();
          if (f.field) newOptions[f.field] = data;
          newOptions[f.api] = data; // also by API URL for reuse elsewhere
        } catch (err) {
          // Don’t block UI; log the error
          console.error(`Failed to load options for ${f.field || f.api}`, err);
        }
      });

      await Promise.allSettled(tasks);
      if (!cancelled) {
        setDropdownOptions((prev) => ({ ...prev, ...newOptions }));
        // Only mark bootstrapping done if formData is also present
        setIsBootstrapping(false);
      }
    };

    fetchDropdowns();
    return () => {
      cancelled = true;
    };
  }, [apiSignature]);

  // ---- Password helpers ----
  const handlePasswordChange = async () => {
    const field = fields.find((f) => f.type === "password");
    if (!field?.ChangePassword || !modelId) return;

    try {
      const res = await fetch(`${field.ChangePassword}/${modelId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ newPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Password updated", { autoClose: 1200 });
        setNewPassword("");
        setShowPasswordChange(false);
      } else {
        toast.error(data?.message || "Error changing password");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error changing password");
    }
  };

  const handleDecrypt = async () => {
    const field = fields.find((f) => f.type === "plainPassword");
    if (!field?.Decrypt || !modelId) return;

    try {
      const res = await fetch(`${field.Decrypt}/${modelId}`);
      const data = await res.json();
      if (res.ok && data?.plainPassword) setDecryptedValue(data.plainPassword);
      else toast.error("Failed to decrypt password");
    } catch (err) {
      console.error(err);
      toast.error("Error decrypting password");
    }
  };

return (
  <div className="edit-form dense">
    {isBootstrapping ? (
      <TableLoader />
    ) : (
      <>
        {/* ---- Title ---- */}
        <h2 className="form-title">{isCreate ? "Create" : "Edit"}</h2>

        {/* ---- Scrollable Body ---- */}
        <div className="form-body">
          <div className="form-grid">
            {(fields || []).map((col, i) => (
              <div
                key={col.field || col.label || i}
                className={`form-field-cell ${col.full ? "full" : ""}`}
              >
                <FieldRenderer
                  col={col}
                  formData={formData}
                  dropdownOptions={dropdownOptions}
                  decryptedValue={decryptedValue}
                  onChange={onChange}
                  setShowPasswordChange={setShowPasswordChange}
                  handleDecrypt={handleDecrypt}
                  isCreate={isCreate}
                  modelName={model}
                />
              </div>
            ))}
          </div>
        </div>

        {/* ---- Actions (fixed inside card bottom) ---- */}
        <div className="form-actions">
          <button
            onClick={handleSubmit}
            className="btn-editForm-submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Submit"}
          </button>
        </div>

        {/* ---- Password Modal ---- */}
        {!isCreate && showPasswordChange && (
          <PasswordModal
            newPassword={newPassword}
            setNewPassword={setNewPassword}
            onCancel={() => setShowPasswordChange(false)}
            onSave={handlePasswordChange}
          />
        )}

        {/* ---- Prev / Next Navigation ---- */}
        {!isCreate && !readOnlyLink && (
          <div className="edit-form-nav-container">
            <DynamicButton
              icon="fa-chevron-left"
              size="md"
              variant="outline"
              onClick={goToPrev}
              disabled={!allIds.length || allIds.indexOf(modelId) === 0}
              title="Go to previous record"
            />
            <DynamicButton
              icon="fa-chevron-right"
              size="md"
              variant="outline"
              onClick={goToNext}
              disabled={
                !allIds.length || allIds.indexOf(modelId) === allIds.length - 1
              }
              title="Go to next record"
            />
          </div>
        )}
      </>
    )}

    <ToastContainer />
  </div>
);

};

export default EditFormComponent;
